import React from 'react';
import PropTypes from 'prop-types';
import {
  Drawer,
  Box,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Toolbar,
  makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import PrintIcon from '@material-ui/icons/Print';

const useStyles = makeStyles((theme) => ({
  drawer: {
    zIndex: 1301,
  },
  drawerPaper: {
    position: 'relative',
    height: '80vh',
    display: 'flex',
    flexDirection: 'column',
  },
  toolbar: {
    borderBottom: '1px solid #ddd',
    backgroundColor: '#002D55',
  },
  toolbarTitle: {
    color: '#FFFFFF',
    fontWeight: 600,
    flex: 1,
  },
  toolbarButton: {
    color: '#FFFFFF',
    marginRight: theme.spacing(1),
    '&:hover': {
      backgroundColor: '#FF7F27',
    },
  },
  pdfContainer: {
    flex: 1,
    overflow: 'auto',
    backgroundColor: '#f5f5f5',
    padding: theme.spacing(2),
  },
  pdfEmbed: {
    width: '100%',
    height: '100%',
    backgroundColor: '#fff',
    borderRadius: 4,
  },
  errorBox: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    color: '#666',
  },
}));

const ReferralLetterViewer = ({ open, onClose, pdfData, fileName = 'referral-letter.pdf' }) => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = React.useState(false);

  const handleDownload = () => {
    if (!pdfData) return;

    // Check if it's already a data URL (base64)
    if (pdfData.startsWith('data:application/pdf')) {
      const link = document.createElement('a');
      link.href = pdfData;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (pdfData.startsWith('http')) {
      // It's a URL, just open it
      window.open(pdfData, '_blank');
    }
  };

  const handlePrint = () => {
    if (!pdfData) return;

    if (pdfData.startsWith('data:application/pdf')) {
      // For base64, we need to convert to blob for printing
      const arr = pdfData.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      const n = bstr.length;
      const u8arr = new Uint8Array(n);
      for (let i = 0; i < n; i++) {
        u8arr[i] = bstr.charCodeAt(i);
      }
      const blob = new Blob([u8arr], { type: mime });
      const url = URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.src = url;
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow.print();
      };
    } else if (pdfData.startsWith('http')) {
      window.open(pdfData, '_blank');
    }
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      classes={{ paper: classes.drawerPaper, root: classes.drawer }}
    >
      <Toolbar className={classes.toolbar}>
        <Typography className={classes.toolbarTitle}>
          {fileName}
        </Typography>
        <Button
          size="small"
          className={classes.toolbarButton}
          startIcon={<GetAppIcon />}
          onClick={handleDownload}
        >
          Download
        </Button>
        <Button
          size="small"
          className={classes.toolbarButton}
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print
        </Button>
        <IconButton
          size="small"
          className={classes.toolbarButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </Toolbar>

      <Box className={classes.pdfContainer}>
        {isLoading && <CircularProgress />}
        {!pdfData ? (
          <Box className={classes.errorBox}>
            <Typography color="textSecondary">No PDF available</Typography>
          </Box>
        ) : (
          <embed
            src={pdfData}
            type="application/pdf"
            className={classes.pdfEmbed}
            onLoad={() => setIsLoading(false)}
            onLoadStart={() => setIsLoading(true)}
          />
        )}
      </Box>
    </Drawer>
  );
};

ReferralLetterViewer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  pdfData: PropTypes.string,
  fileName: PropTypes.string,
};

export default ReferralLetterViewer;
